import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone, PhoneOff, PhoneOutgoing, PhoneIncoming,
  Mic, Clock
} from 'lucide-react';
import { getApiUrl } from '../utils/apiConfig';

interface Contact {
  id: string;
  name: string;
  company?: string;
  phone: string;
  lastCall?: string;
  favorite?: boolean;
}

interface RecentCall {
  id: string;
  name: string;
  phone: string;
  time: string;
  duration: string;
  type: 'incoming' | 'outgoing' | 'missed';
  status: 'completed' | 'failed';
}

const VoiceCallCenter: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dial' | 'recent' | 'voicemail'>('dial');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isCallActive, setIsCallActive] = useState(false);
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('kentrill');
  const [voiceOptions, setVoiceOptions] = useState<any[]>([]);
  const [selectedObjective, setSelectedObjective] = useState('site_visit');
  const [selectedApproach, setSelectedApproach] = useState('premium');
  const [customObjective, setCustomObjective] = useState('');
  const [customApproach, setCustomApproach] = useState('');
  const [showCustomObjective, setShowCustomObjective] = useState(false);
  const [showCustomApproach, setShowCustomApproach] = useState(false);

  // Live transcript states
  const [isRecording, setIsRecording] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [transcripts, setTranscripts] = useState<any>({});
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isLoadingTranscripts, setIsLoadingTranscripts] = useState(false);

  // Fetch available voices
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/vapi/voices`);
        const data = await response.json();
        if (data.success && data.voices) {
          setVoiceOptions(data.voices);
          if (data.voices.length > 0) {
            setSelectedVoice(data.voices[0].value);
          }
        }
      } catch (error) {
        console.error('Failed to fetch voices:', error);
      }
    };
    fetchVoices();
  }, []);

  // Fetch real VAPI transcripts
  const fetchTranscripts = async () => {
    try {
      setIsLoadingTranscripts(true);
      const response = await fetch(`${getApiUrl()}/api/vapi/transcripts`);
      const data = await response.json();
      if (data.success) {
        setTranscripts(data.transcripts);
        // Auto-select first call if no call is selected
        if (!selectedCallId && Object.keys(data.transcripts).length > 0) {
          setSelectedCallId(Object.keys(data.transcripts)[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch transcripts:', error);
    } finally {
      setIsLoadingTranscripts(false);
    }
  };

  // Load transcripts when transcript tab is activated
  useEffect(() => {
    if (activeTab === 'voicemail') {
      fetchTranscripts();
    }
  }, [activeTab]);

  // Sample data
  const recentCalls: RecentCall[] = [
    { id: '1', name: 'John Construction LLC', phone: '+1 (555) 123-4567', time: '10:30 AM', duration: '5:23', type: 'outgoing', status: 'completed' },
    { id: '2', name: 'Sarah Williams', phone: '+1 (555) 987-6543', time: '9:15 AM', duration: '3:45', type: 'incoming', status: 'completed' },
    { id: '3', name: 'Mike\'s Plumbing', phone: '+1 (555) 456-7890', time: 'Yesterday', duration: '-', type: 'missed', status: 'failed' }
  ];

  const favoriteContacts: Contact[] = [
    { id: '1', name: 'David Chen', company: 'Chen Electric', phone: '+1 (555) 111-2222', favorite: true },
    { id: '2', name: 'Maria Garcia', company: 'MG Flooring', phone: '+1 (555) 333-4444', favorite: true },
    { id: '3', name: 'Robert Johnson', company: 'RJ Roofing', phone: '+1 (555) 555-6666', favorite: true }
  ];

  // Sample transcript data
  const sampleTranscripts = [
    {
      id: '1',
      callId: 'call_001',
      contact: 'Smith Construction',
      phone: '+1 (555) 123-4567',
      timestamp: '2025-01-15 14:30:00',
      duration: '4:23',
      type: 'outgoing',
      status: 'completed',
      objective: 'Site Visit',
      transcript: [
        { speaker: 'AI', text: "Hi, I'm calling from HomeQuest about the downtown commercial project. Is this a good time to talk?", timestamp: '00:00' },
        { speaker: 'Contact', text: "Yes, perfect timing! I was just reviewing your project details.", timestamp: '00:05' },
        { speaker: 'AI', text: "Great! We're looking for a contractor who can handle the structural work. When would be a good time for a site visit?", timestamp: '00:12' },
        { speaker: 'Contact', text: "I'm available Thursday morning. How about 10 AM? I'd like to bring my project manager too.", timestamp: '00:18' },
        { speaker: 'AI', text: "Thursday at 10 AM works perfectly. Should I send you the site address and parking details?", timestamp: '00:25' },
        { speaker: 'Contact', text: "Yes please, and if you have any preliminary drawings, that would be helpful too.", timestamp: '00:31' }
      ]
    },
    {
      id: '2',
      callId: 'call_002',
      contact: 'Elite Roofing',
      phone: '+1 (555) 987-6543',
      timestamp: '2025-01-15 11:15:00',
      duration: '2:47',
      type: 'incoming',
      status: 'completed',
      objective: 'Quote Response',
      transcript: [
        { speaker: 'Contact', text: "Hi, this is Mike from Elite Roofing. I got your message about the residential project.", timestamp: '00:00' },
        { speaker: 'AI', text: "Thanks for calling back, Mike! Yes, we have a 3,200 sq ft roof that needs replacement. What's your availability?", timestamp: '00:06' },
        { speaker: 'Contact', text: "We're pretty booked, but I could squeeze it in next month. What's your timeline?", timestamp: '00:13' },
        { speaker: 'AI', text: "Next month could work. What would your quote be for tear-off and new installation?", timestamp: '00:19' },
        { speaker: 'Contact', text: "For that size, probably around $18,000 to $22,000 depending on materials. I'd need to see it first.", timestamp: '00:25' }
      ]
    }
  ];


  // Dial pad
  const dialPadNumbers = [
    '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'
  ];

  const handleDial = (num: string) => {
    setPhoneNumber(prev => prev + num);
  };

  const makeCall = async () => {
    if (!phoneNumber) return;
    
    setIsCallActive(true);
    try {
      // Import the supabase utilities
      const { supabase, getCurrentUser } = await import('../utils/supabaseClient');
      
      let userProfile = null;
      let projectData: any = {};
      
      // Get the authenticated user
      let user = await getCurrentUser();
      
      // If no Supabase user but we have Ken White session, use it
      if (!user) {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            if (userData.email === 'kenwhite2015@gmail.com') {
              console.log('Using Ken White session from localStorage');
              user = userData;
            }
          } catch (e) {
            console.log('Could not parse stored user:', e);
          }
        }
      }
      
      console.log('Supabase user:', user ? 'Found' : 'Not found');
      console.log('User ID:', user?.id);
      
      if (user) {
        // Use the new team-based functions to get data
        try {
          // Get team info using the function we created
          const { data: teamInfo, error: teamError } = await supabase
            .rpc('get_team_info', { user_id: user.id });
          
          if (teamError) {
            console.error('Error fetching team info:', teamError);
          }
          
          if (teamInfo && teamInfo.length > 0) {
            const team = teamInfo[0];
            console.log('Team info from database:', team);
            console.log('Team company_name:', team.company_name);
            console.log('Team name:', team.team_name);
            
            // Get user's profile
            const { data: profile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', user.id)
              .single();
            
            userProfile = {
              id: user.id,
              name: profile?.full_name || `${profile?.first_name} ${profile?.last_name}`.trim() || user.fullName || user.name,
              companyName: team.company_name || team.team_name || user.companyName,
              companyPhone: team.company_phone,
              phone: profile?.phone_number,
              teamId: team.team_id
            };
            
            console.log('Using team-based profile:', userProfile);
            
            // Get active project for the team
            const { data: activeProject, error: projectError } = await supabase
              .rpc('get_active_project', { p_user_id: user.id });
            
            console.log('Project fetch result:', { activeProject, projectError, userId: user.id });
            
            if (activeProject && activeProject.length > 0) {
              const project = activeProject[0];
              projectData = {
                address: project.project_address,
                budget: project.project_budget,
                type: project.project_type || 'Construction',
                name: project.project_name,
                timeline: project.timeline,
                sqft: project.square_footage
              };
              console.log('Using active team project:', projectData);
            } else {
              console.log('No active project found for user');
            }
          } else {
            console.log('No team found for user, using individual profile or stored data');
            
            // Try to get from profiles table first
            const { data: profile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', user.id)
              .single();
            
            if (profile) {
              userProfile = {
                id: profile.id,
                name: profile.full_name || `${profile.first_name} ${profile.last_name}`.trim(),
                companyName: profile.company_name,
                phone: profile.phone_number,
                teamId: profile.team_id
              };
            }
            
            // If still no profile, use stored user data
            if (!userProfile && user) {
              userProfile = {
                id: user.id,
                name: user.fullName || user.name || 'Ken White',
                companyName: user.companyName || 'HomeQuest',
                phone: user.phone,
                teamId: user.teamId || user.team_id
              };
              console.log('Using stored user data:', userProfile);
            }
          }
        } catch (e) {
          console.error('Error fetching team data:', e);
        }
      } else {
        console.log('No authenticated user - please log in to make calls with your company information');
      }
      
      const callData = {
        to: phoneNumber.startsWith('+1') ? phoneNumber : `+1${phoneNumber}`,
        vendorName: 'the vendor',
        vendorCompany: 'contractor services',
        projectDetails: {
          address: projectData.address || 'Project Site',
          budget: projectData.budget && projectData.budget > 0 ? `$${projectData.budget.toLocaleString()}` : 'TBD',
          type: projectData.type || 'Construction',
          name: projectData.name || 'New Project',
          timeline: projectData.timeline || '6 months',
          sqft: projectData.sqft ? `${projectData.sqft.toLocaleString()} sq ft` : undefined,
          urgency: 'immediate',
          specificWork: 'Full project services'
        },
        userId: userProfile?.id,
        companyId: userProfile?.companyId,
        builderName: userProfile?.name,
        builderPhone: userProfile?.phone,
        companyName: userProfile?.companyName,
        companyPhone: userProfile?.companyPhone,
        teamId: userProfile?.teamId,
        voiceId: selectedVoice,
        briefing: {
          objective: customObjective || (
                     selectedObjective === 'site_visit' ? 'site_visit' :
                     selectedObjective === 'get_quote' ? 'get_quote' :
                     selectedObjective === 'check_availability' ? 'check_availability' :
                     selectedObjective === 'gauge_interest' ? 'gauge_interest' :
                     selectedObjective === 'schedule_work' ? 'schedule_work' :
                     selectedObjective === 'check_progress' ? 'check_progress' :
                     selectedObjective === 'discuss_changes' ? 'discuss_changes' :
                     selectedObjective === 'coordinate_delivery' ? 'coordinate_delivery' :
                     'general'),
          approach: customApproach || (
                    selectedApproach === 'premium' ? 'professional' :
                    selectedApproach === 'urgent' ? 'urgent' :
                    selectedApproach === 'relationship' ? 'relationship' :
                    selectedApproach === 'direct' ? 'direct' :
                    selectedApproach === 'budget_conscious' ? 'consultative' :
                    selectedApproach === 'partnership' ? 'relationship' :
                    selectedApproach === 'professional' ? 'professional' :
                    'professional')
        }
      };
      
      console.log('Full call data being sent:', callData);
      console.log('IMPORTANT - Company Name:', callData.companyName);
      console.log('IMPORTANT - Builder Name:', callData.builderName);
      console.log('IMPORTANT - User Profile:', userProfile);
      
      const response = await fetch(`${getApiUrl()}/api/vapi/call`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(callData)
      });
      
      const data = await response.json();
      if (data.success) {
        setActiveCallId(data.callId);
      } else {
        // Show error to user
        alert(`Call failed: ${data.error || data.message || 'Unknown error'}`);
        console.error('Call failed:', data);
      }
    } catch (error) {
      console.error('Call failed:', error);
      alert('Failed to initiate call. Please check console for details.');
    }
  };

  const endCall = async () => {
    if (!activeCallId) return;
    
    try {
      await fetch(`${getApiUrl()}/api/vapi/hangup/${activeCallId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Hangup failed:', error);
    } finally {
      setIsCallActive(false);
      setActiveCallId(null);
      setPhoneNumber('');
    }
  };

  return (
    <div className="bg-gray-900/50 rounded-xl p-3">
        {/* Clean Header */}
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-4">
            <h2 className="text-base font-semibold text-white">Phone</h2>
            {/* Quick Voice Selector */}
            <div className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-gray-400" />
              <select 
                value={selectedVoice}
                onChange={(e) => setSelectedVoice(e.target.value)}
                className="bg-gray-800 text-white text-xs px-2 py-1 rounded border border-gray-700"
              >
                {voiceOptions.map(v => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>
        
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-3 bg-gray-800/50 p-1 rounded">
        {[
          { id: 'dial', label: 'Dial', icon: Phone },
          { id: 'recent', label: 'Recent', icon: Clock },
          { id: 'voicemail', label: 'Transcript', icon: Mic }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded text-sm font-medium transition-all ${
              activeTab === tab.id 
                ? 'bg-purple-600 text-white' 
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="grid grid-cols-12 gap-3">
        {/* Main Content - Full Width */}
        <div className="col-span-12">
          {activeTab === 'dial' ? (
            <div className="bg-gray-800/30 rounded-lg p-4">
              {/* Number Display */}
              <div className="mb-4">
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && phoneNumber && !isCallActive) {
                      makeCall();
                    }
                  }}
                  placeholder="Enter number or start dialing"
                  className="w-full text-base font-light text-center bg-transparent text-white placeholder-gray-600 focus:outline-none py-2"
                />
              </div>

              {/* Call Setup */}
              <div className="mb-6 bg-gradient-to-br from-gray-900/70 to-gray-800/50 rounded-xl p-6 border border-gray-600/30 backdrop-blur-sm shadow-2xl">

                {/* Call Objective Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-200 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                      Call Objective
                    </label>
                    <button
                      onClick={() => {
                        setShowCustomObjective(!showCustomObjective);
                        if (showCustomObjective) {
                          setCustomObjective('');
                        }
                      }}
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium px-2 py-1 rounded-md hover:bg-blue-500/10"
                    >
                      {showCustomObjective ? 'Use Presets' : 'Custom'}
                    </button>
                  </div>

                  {showCustomObjective ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={customObjective}
                        onChange={(e) => setCustomObjective(e.target.value)}
                        placeholder="Describe your specific call objective..."
                        className="w-full px-4 py-3 bg-gray-800/60 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all text-sm"
                      />
                      <p className="text-xs text-gray-500">AI will use this custom objective to tailor the conversation</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: 'site_visit', label: 'Site Visit', desc: 'Schedule inspection', icon: '📍' },
                        { id: 'get_quote', label: 'Get Quote', desc: 'Request pricing', icon: '💰' },
                        { id: 'check_availability', label: 'Availability', desc: 'Check schedule', icon: '📅' },
                        { id: 'gauge_interest', label: 'Gauge Interest', desc: 'Assess opportunity', icon: '🎯' },
                        { id: 'schedule_work', label: 'Schedule Work', desc: 'Book project', icon: '⚡' },
                        { id: 'check_progress', label: 'Check Progress', desc: 'Status update', icon: '📊' },
                        { id: 'discuss_changes', label: 'Changes', desc: 'Modify scope', icon: '🔄' },
                        { id: 'coordinate_delivery', label: 'Delivery', desc: 'Coordinate logistics', icon: '🚚' }
                      ].map((obj) => (
                        <button
                          key={obj.id}
                          onClick={() => {
                            setSelectedObjective(obj.id);
                            setCustomObjective('');
                          }}
                          className={`px-3 py-3 text-left rounded-lg transition-all duration-200 group ${
                            selectedObjective === obj.id
                              ? 'bg-gradient-to-br from-purple-600 to-purple-700 text-white shadow-lg shadow-purple-500/30'
                              : 'bg-gray-800/60 text-gray-300 hover:bg-gray-700/80 border border-gray-600/30 hover:border-gray-500/50'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm">{obj.icon}</span>
                            <div className="text-xs font-medium">{obj.label}</div>
                          </div>
                          <div className="text-[10px] opacity-70">{obj.desc}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Communication Style Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-200 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                      Communication Style
                    </label>
                    <button
                      onClick={() => {
                        setShowCustomApproach(!showCustomApproach);
                        if (showCustomApproach) {
                          setCustomApproach('');
                        }
                      }}
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium px-2 py-1 rounded-md hover:bg-blue-500/10"
                    >
                      {showCustomApproach ? 'Use Presets' : 'Custom'}
                    </button>
                  </div>

                  {showCustomApproach ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={customApproach}
                        onChange={(e) => setCustomApproach(e.target.value)}
                        placeholder="Describe your preferred communication style..."
                        className="w-full px-4 py-3 bg-gray-800/60 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all text-sm"
                      />
                      <p className="text-xs text-gray-500">AI will adapt its tone and approach based on this input</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: 'premium', label: 'Premium', desc: 'High-end projects', icon: '💎' },
                        { id: 'urgent', label: 'Urgent', desc: 'Time-sensitive', icon: '⏰' },
                        { id: 'relationship', label: 'Relationship', desc: 'Build rapport', icon: '🤝' },
                        { id: 'direct', label: 'Direct', desc: 'Straight to point', icon: '🎯' },
                        { id: 'budget_conscious', label: 'Budget Focus', desc: 'Cost-effective', icon: '💡' },
                        { id: 'partnership', label: 'Partnership', desc: 'Long-term', icon: '🏆' },
                        { id: 'professional', label: 'Professional', desc: 'Formal tone', icon: '👔' }
                      ].map((approach) => (
                        <button
                          key={approach.id}
                          onClick={() => {
                            setSelectedApproach(approach.id);
                            setCustomApproach('');
                          }}
                          className={`px-3 py-3 text-left rounded-lg transition-all duration-200 group ${
                            selectedApproach === approach.id
                              ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                              : 'bg-gray-800/60 text-gray-300 hover:bg-gray-700/80 border border-gray-600/30 hover:border-gray-500/50'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm">{approach.icon}</span>
                            <div className="text-xs font-medium">{approach.label}</div>
                          </div>
                          <div className="text-[10px] opacity-70">{approach.desc}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Dial Pad Grid */}
              <div className="grid grid-cols-3 gap-2 max-w-[240px] mx-auto mb-4">
                {dialPadNumbers.map((num) => (
                  <button
                    key={num}
                    onClick={() => handleDial(num)}
                    className="aspect-square bg-gray-800 hover:bg-gray-700 rounded-lg text-base font-light text-white transition-all hover:scale-105 active:scale-95 py-3"
                  >
                    {num}
                  </button>
                ))}
              </div>

              {/* Call Button */}
              <div className="flex justify-center">
                {isCallActive ? (
                  <button
                    onClick={endCall}
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-full font-medium transition-all flex items-center gap-2 shadow-lg hover:shadow-red-600/25 text-sm"
                  >
                    <PhoneOff className="w-5 h-5" />
                    End Call
                  </button>
                ) : (
                  <button
                    onClick={makeCall}
                    disabled={!phoneNumber}
                    className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-full font-medium transition-all flex items-center gap-2 shadow-lg hover:shadow-green-600/25 text-sm"
                  >
                    <Phone className="w-5 h-5" />
                    Call with AI
                  </button>
                )}
              </div>
            </div>
          ) : activeTab === 'recent' ? (
            <div className="bg-gray-800/30 rounded-lg p-4">
              <h3 className="text-base font-semibold text-white mb-3">Recent Calls</h3>
              <div className="space-y-2">
                {recentCalls.map((call) => (
                  <div key={call.id} className="flex items-center justify-between p-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        call.type === 'incoming' ? 'bg-blue-900/30' :
                        call.type === 'outgoing' ? 'bg-green-900/30' :
                        'bg-red-900/30'
                      }`}>
                        {call.type === 'incoming' ? <PhoneIncoming className="w-4 h-4 text-blue-400" /> :
                         call.type === 'outgoing' ? <PhoneOutgoing className="w-4 h-4 text-green-400" /> :
                         <PhoneOff className="w-4 h-4 text-red-400" />}
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{call.name}</p>
                        <p className="text-xs text-gray-400">{call.phone}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-400">{call.time}</p>
                      {call.duration !== '-' && (
                        <p className="text-xs text-gray-500">{call.duration}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-gray-800/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-white">Live Call Transcripts</h3>
                <button
                  onClick={fetchTranscripts}
                  disabled={isLoadingTranscripts}
                  className="px-3 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white text-xs rounded-md transition-colors"
                >
                  {isLoadingTranscripts ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {isLoadingTranscripts ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                  <p className="text-gray-400 text-sm">Loading transcripts...</p>
                </div>
              ) : Object.keys(transcripts).length === 0 ? (
                <div className="text-center py-8">
                  <Mic className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">No call transcripts available</p>
                  <p className="text-gray-500 text-xs mt-1">Transcripts will appear here after VAPI calls</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Call List */}
                  <div className="lg:col-span-1">
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Recent Calls</h4>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {Object.keys(transcripts).map((callId) => {
                        const transcriptSegments = transcripts[callId];
                        const firstSegment = transcriptSegments[0];
                        const callTime = firstSegment ? new Date(firstSegment.spoken_at).toLocaleTimeString() : 'Unknown';
                        const callerName = firstSegment?.caller_name;
                        const callerPhone = firstSegment?.caller_phone;

                        return (
                          <button
                            key={callId}
                            onClick={() => setSelectedCallId(callId)}
                            className={`w-full text-left p-3 rounded-lg transition-all ${
                              selectedCallId === callId
                                ? 'bg-purple-600/20 border border-purple-500/50'
                                : 'bg-gray-800/50 hover:bg-gray-700/50 border border-gray-600/30'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-white truncate">
                                {callerName || `Call ${callId.substring(0, 8)}...`}
                              </span>
                              <span className="text-xs text-gray-400">{callTime}</span>
                            </div>
                            <p className="text-xs text-gray-400">
                              {callerPhone ? callerPhone : `${transcriptSegments.length} segments`}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Transcript Display */}
                  <div className="lg:col-span-2">
                    {selectedCallId && transcripts[selectedCallId] ? (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="text-sm font-medium text-gray-300">
                              Call Transcript - {transcripts[selectedCallId][0]?.caller_name || selectedCallId.substring(0, 8) + '...'}
                            </h4>
                            {transcripts[selectedCallId][0]?.caller_phone && (
                              <p className="text-xs text-gray-400 mt-1">
                                {transcripts[selectedCallId][0].caller_phone}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-gray-400">
                            {transcripts[selectedCallId].length} segments
                          </span>
                        </div>

                        <div className="bg-gray-900/50 rounded-lg p-4 max-h-96 overflow-y-auto space-y-3">
                          {transcripts[selectedCallId].map((segment: any, index: number) => (
                            <div
                              key={index}
                              className={`flex gap-3 ${
                                segment.speaker === 'assistant' ? 'justify-end' : 'justify-start'
                              }`}
                            >
                              <div
                                className={`max-w-[80%] rounded-lg p-3 ${
                                  segment.speaker === 'assistant'
                                    ? 'bg-purple-600/20 text-purple-100 border border-purple-500/30'
                                    : 'bg-blue-600/20 text-blue-100 border border-blue-500/30'
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium opacity-80">
                                    {segment.speaker === 'assistant' ? '🤖 AI' : '👤 Human'}
                                  </span>
                                  <span className="text-xs opacity-60">
                                    {new Date(segment.spoken_at).toLocaleTimeString()}
                                  </span>
                                  {segment.confidence && (
                                    <span className="text-xs opacity-60">
                                      {Math.round(segment.confidence * 100)}%
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm">{segment.text}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-400 text-sm">Select a call to view transcript</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Side Panel - Call Stats - Hidden for compact view */}
        <div className="col-span-12 lg:col-span-4 hidden">
          <div className="bg-gray-800/30 rounded-xl p-3">
            <h3 className="text-sm font-semibold text-white mb-2">Today's Activity</h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <PhoneOutgoing className="w-5 h-5 text-green-400" />
                  <span className="text-gray-300">Outgoing</span>
                </div>
                <span className="text-white font-semibold">8</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <PhoneIncoming className="w-5 h-5 text-blue-400" />
                  <span className="text-gray-300">Incoming</span>
                </div>
                <span className="text-white font-semibold">5</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-purple-400" />
                  <span className="text-gray-300">Total Time</span>
                </div>
                <span className="text-white font-semibold">2h 34m</span>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Success Rate</span>
                <span className="text-sm text-white">92%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full" style={{ width: '92%' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceCallCenter;